'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Bot, BookOpen } from 'lucide-react';
import Badge from '@/components/ui/Badge';

const children = [
  {
    id: 'child-001',
    name: 'Amara Osei',
    age: 13,
    ageGroup: '10–13',
    avatar: '🌟',
    streak: 12,
    totalBadges: 18,
    weeklyHours: 4.2,
    courses: [
      { id: 'cp-001', name: 'Intro to Coding', wing: 'intermediate', progress: 68, status: 'active' },
      { id: 'cp-002', name: 'Earth & Space Science', wing: 'intermediate', progress: 42, status: 'active' },
      { id: 'cp-003', name: 'Logic & Problem Solving', wing: 'intermediate', progress: 91, status: 'nearly-done' },
      { id: 'cp-004', name: 'Advanced Mathematics', wing: 'advanced', progress: 0, status: 'locked' },
    ],
  },
  {
    id: 'child-002',
    name: 'Kofi Osei',
    age: 8,
    ageGroup: '6–9',
    avatar: '🚀',
    streak: 5,
    totalBadges: 7,
    weeklyHours: 3.2,
    courses: [
      { id: 'cp-005', name: 'Basic Mathematics', wing: 'foundations', progress: 74, status: 'active' },
      { id: 'cp-006', name: 'Reading & Writing Essentials', wing: 'foundations', progress: 55, status: 'active' },
      { id: 'cp-007', name: 'Science Basics', wing: 'foundations', progress: 22, status: 'active' },
    ],
  },
];

const wingProgressColor: Record<string, string> = {
  foundations: 'bg-emerald-500',
  intermediate: 'bg-blue-500',
  advanced: 'bg-purple-500',
  professional: 'bg-amber-500',
};

export default function ParentChildProgress() {
  const [expanded, setExpanded] = useState<string | null>('child-001');

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <h2 className="text-lg font-bold text-foreground mb-5">Children's Progress</h2>
      <div className="space-y-4">
        {children.map((child) => {
          const isExpanded = expanded === child.id;
          return (
            <div key={child.id} className="border border-border rounded-xl overflow-hidden">
              {/* Child header */}
              <button
                onClick={() => setExpanded(isExpanded ? null : child.id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors text-left"
              >
                <span className="text-3xl shrink-0">{child.avatar}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-foreground">{child.name}</p>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Age {child.age} · {child.ageGroup}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">🔥 {child.streak}-day streak</span>
                    <span className="flex items-center gap-1">🏆 {child.totalBadges} badges</span>
                    <span className="flex items-center gap-1"><BookOpen size={11} />{child.courses.length} courses</span>
                    <span className="flex items-center gap-1"><Bot size={11} />{child.weeklyHours}h this week</span>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp size={16} className="text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                )}
              </button>

              {/* Expanded courses */}
              {isExpanded && (
                <div className="border-t border-border divide-y divide-border fade-in">
                  {child.courses.map((course) => (
                    <div key={course.id} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <p className="text-sm font-semibold text-foreground">{course.name}</p>
                          <Badge variant={course.wing as 'foundations' | 'intermediate' | 'advanced' | 'professional'}>
                            {course.wing.charAt(0).toUpperCase() + course.wing.slice(1)}
                          </Badge>
                          {course.status === 'locked' && (
                            <span className="text-xs text-amber-600 font-medium">🔒 Locked</span>
                          )}
                          {course.status === 'nearly-done' && (
                            <span className="text-xs text-emerald-600 font-medium">✓ Almost done</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full progress-bar-fill ${
                                course.status === 'locked' ? 'bg-muted-foreground/20' : wingProgressColor[course.wing]
                              }`}
                              style={{ width: `${course.progress}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-muted-foreground font-tabular shrink-0 w-8 text-right">
                            {course.progress}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}